"use client";

import { useEffect, useRef } from "react";

export default function MembershipCTA() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.2 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => { if (sectionRef?.current) observer?.unobserve(sectionRef?.current); };
  }, []);

  return (
    <section
      id="membership"
      ref={sectionRef}
      className="relative py-16 sm:py-24 md:py-32 overflow-hidden grain-overlay"
      style={{ background: "var(--navy-dark)" }}
    >
      {/* Radial spotlight */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 50% 50%, rgba(224,96,49,0.18) 0%, rgba(27,54,93,0.4) 40%, transparent 70%)",
        }}
      />
      {/* Orbit rings */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
        style={{
          width: "600px",
          height: "600px",
          border: "1px solid rgba(224,96,49,0.08)",
          animation: "orbit-slow 25s linear infinite",
        }}
      />
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full pointer-events-none"
        style={{
          width: "900px",
          height: "900px",
          border: "1px solid rgba(224,96,49,0.04)",
          animation: "orbit-slow 40s linear infinite reverse",
        }}
      />
      {/* Athletic lines */}
      {[
        { w: "180px", t: "20%", l: "5%", d: "0s" },
        { w: "120px", t: "70%", l: "90%", d: "2s" },
        { w: "90px", t: "85%", l: "10%", d: "1s" },
      ]?.map((line, i) => (
        <div
          key={i}
          className="athletic-line"
          style={{ width: line?.w, top: line?.t, left: line?.l, animationDelay: line?.d }}
        />
      ))}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
        <p className="section-label mb-4 sm:mb-6">Ready to Start?</p>

        <h2 className="headline-xl text-white mb-4 sm:mb-6">
          Train Like a{" "}
          <span className="gradient-text">Champion.</span>
        </h2>

        <p className="text-white/60 text-base sm:text-xl max-w-2xl mx-auto mb-8 sm:mb-12 leading-relaxed">
          Join hundreds of athletes, professionals, and families who've made Play.XP their performance home. 
          Your best session is waiting.
        </p>

        {/* Features row */}
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center justify-center gap-3 sm:gap-6 mb-10 sm:mb-14">
          {[
            "Professional Coaching",
            "World-Class Facilities",
            "All Ages Welcome",
            "Flexible Membership",
          ]?.map((feature) => (
            <div key={feature} className="flex items-center gap-2">
              <svg className="w-4 h-4 flex-shrink-0" fill="var(--terracotta)" viewBox="0 0 24 24">
                <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-white/70 text-xs sm:text-sm font-medium text-left">{feature}</span>
            </div>
          ))}
        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-5">
          <a href="#contact" className="btn-primary pulse-cta text-sm sm:text-base px-6 sm:px-10 py-3 sm:py-4 w-full sm:w-auto justify-center">
            <span>Join the Revolution</span>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
            </svg>
          </a>
          <a href="#contact" className="btn-secondary text-sm sm:text-base px-6 sm:px-10 py-3 sm:py-4 w-full sm:w-auto justify-center">
            <span>Book a Session</span>
          </a>
        </div>
      </div>
    </section>
  );
}